import { apiClient } from "../api/apiClient.js";

// Thin fetch wrappers over the Prompt 099 Ready For Match API. Every query
// param is optional - visibility scoping (Agent/PLC/SuperAgent/SuperAdmin)
// always happens on the backend from the caller's own session, never from a
// value this file sends. This file never calls the Worker Profile create
// endpoint, the e-KYC decision endpoint, or any matching/assignment/
// tracking/buddy/invite-card API - none of those are in scope for this
// packet, and marking a worker Ready For Match here never implies any of
// them happened.

const CANDIDATES_LIST_PATH = "/backoffice/ready-for-match/candidates";

// The mark-ready action lives under the Worker Profile resource path in the
// real backend (BackofficeReadyForMatchController maps
// POST /api/backoffice/worker-profiles/{workerId}/ready-for-match), not
// under /ready-for-match/candidates/{workerId} - that second path is
// GET-only (candidate detail). Keeping this as its own constant instead of
// building it off CANDIDATES_LIST_PATH avoids ever accidentally reusing the
// wrong prefix for the write action.
const WORKER_PROFILES_PATH = "/backoffice/worker-profiles";

// Exactly the param names the P099 contract whitelists - anything else
// passed to listReadyForMatchCandidates is silently dropped rather than
// forwarded, so a typo'd key (or a future filter added to the UI without
// updating this whitelist) can never accidentally reach the backend as an
// unfiltered pass-through.
const LIST_PARAM_KEYS = [
  "page",
  "pageSize",
  "search",
  "workerStatus",
  "complianceStatus",
  "nationality",
  "preferredLanguageCode",
  "agentId",
  "superAgentOrganizationId",
  "plcOrganizationId",
];

/** Builds a query string with every empty/null/undefined value omitted entirely (never sent as `key=`). */
function buildListQuery(params = {}) {
  const searchParams = new URLSearchParams();
  for (const key of LIST_PARAM_KEYS) {
    const value = params[key];
    if (value === null || value === undefined || value === "") {
      continue;
    }
    searchParams.set(key, String(value));
  }
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export function listReadyForMatchCandidates(params = {}) {
  return apiClient.get(`${CANDIDATES_LIST_PATH}${buildListQuery(params)}`);
}

// Detail fetch requires a real, non-empty workerId - this file never issues
// a request for a missing/blank id (the caller only calls this once a
// row/card is actually clicked).
export function getReadyForMatchCandidate(workerId) {
  if (!workerId) {
    throw new Error("getReadyForMatchCandidate requires a workerId");
  }
  return apiClient.get(`${CANDIDATES_LIST_PATH}/${encodeURIComponent(workerId)}`);
}

// The one write action this file exposes. Requires a real, non-empty
// workerId for the same reason as getReadyForMatchCandidate above - never
// fires without a real, already-selected candidate. Sends no request body:
// the real endpoint (POST .../worker-profiles/{workerId}/ready-for-match)
// takes no payload, only the id in the path. This call updates the worker
// profile's readiness flag only - it never creates a WorkerAssignment row
// and never starts tracking/device binding, regardless of what the backend
// response looks like.
export function markReadyForMatch(workerId) {
  if (!workerId) {
    throw new Error("markReadyForMatch requires a workerId");
  }
  return apiClient.post(`${WORKER_PROFILES_PATH}/${encodeURIComponent(workerId)}/ready-for-match`);
}
